import { useMemo, useState } from 'react'
import { Link } from 'react-router'
import { ChevronRight } from 'lucide-react'
import { TransactionListItem } from '@/components/transaction/transaction-list-item'
import { SpendingCalendar } from '@/components/calendar/spending-calendar'
import { SpendingBreakdown } from '@/components/spending-breakdown'
import { MonthSelector } from '@/components/month-selector'
import { Skeleton } from '@/components/ui/skeleton'
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { BudgetDetailSheet } from '@/components/budget/budget-detail-sheet'
import {
  CategoryDetailSheet,
  CategorySummarySection,
  CategorySubcategorySection,
  CategoryTopTransactionsSection,
  CategoryTransactionsFooter,
} from '@/components/report/category-detail'
import { useTransactions, useBudgetStatus, useWeeklyViewStyle, useCategories, useScope, useExpenseType, useProfiles, usePaidBy } from '@/lib/hooks'
import { buildCategoryDrilldown } from '@/lib/statistics'
import { DEFAULT_CATEGORY_COLOR } from '@/lib/constants'

import type { BudgetStatusItem } from '@/lib/api/budgets'
import { FilterChip } from '@/components/filter-chip'
import { SCOPE_LABEL, SCOPE_OPTIONS, EXPENSE_TYPE_LABEL, EXPENSE_TYPE_OPTIONS, PAID_BY_LABEL, buildPaidByOptions } from '@/components/filter-chip-options'
import { getCurrentMonth, formatAmount, getWeeksInMonth, filterByExpenseType, filterByPaidBy } from '@/lib/utils'
export default function HomePage() {
  const [month, setMonth] = useState(getCurrentMonth())
  const isCurrentMonth = month === getCurrentMonth()
  const { scope, setScope } = useScope()
  const { expenseType, setExpenseType } = useExpenseType()
  const { paidBy, setPaidBy } = usePaidBy()
  const { data: profiles = [] } = useProfiles()
  const paidByOptions = useMemo(() => buildPaidByOptions(profiles), [profiles])

  const { data: budgetItems = [], isLoading: budgetLoading } = useBudgetStatus(month, expenseType)
  const { data: transactions = [], isLoading: txLoading } = useTransactions(month)
  const { data: allCategories = [] } = useCategories()

  // Aggregate subcategory budget rows into parent-level display rows
  const displayBudgetItems = useMemo(() => {
    const parentItems = budgetItems.filter(item => !item.is_subcategory_budget)
    const subItems = budgetItems.filter(item => item.is_subcategory_budget)

    // Group subcategory items by parent_category_id
    const subByParent = new Map<string, BudgetStatusItem[]>()
    for (const item of subItems) {
      if (!item.parent_category_id) continue
      const group = subByParent.get(item.parent_category_id) ?? []
      group.push(item)
      subByParent.set(item.parent_category_id, group)
    }

    // Create synthetic parent rows for parents with subcategory budgets
    // Only budgeted subcategories (budget_amount > 0) contribute to budget/spent totals
    const syntheticParents: BudgetStatusItem[] = []
    for (const [parentId, subs] of subByParent) {
      const budgetedSubs = subs.filter(i => i.budget_amount > 0)
      const totalBudget = budgetedSubs.reduce((s, i) => s + i.budget_amount, 0)
      const totalSpent = budgetedSubs.reduce((s, i) => s + i.spent_amount, 0)
      const parentCat = allCategories.find(c => c.id === parentId)
      syntheticParents.push({
        category_id: parentId,
        category_name: parentCat?.name ?? subs[0].category_name,
        budget_amount: totalBudget,
        spent_amount: totalSpent,
        remaining: totalBudget - totalSpent,
        sort_order: parentCat?.sort_order ?? subs[0].sort_order,
        color: parentCat?.color ?? subs[0].color,
        parent_category_id: null,
        is_subcategory_budget: false,
      })
    }

    return [...parentItems, ...syntheticParents].sort((a, b) => a.sort_order - b.sort_order)
  }, [budgetItems, allCategories])

  const loading = budgetLoading || txLoading
  const filtered = filterByPaidBy(filterByExpenseType(transactions, expenseType), paidBy)

  const [weeklyViewStyle] = useWeeklyViewStyle()

  const recentTransactions = filtered.slice(0, 5)

  // 表示中の月の日数と経過日数（予算ペース計算用）
  const daysInDisplayMonth = useMemo(() => {
    const [y, m] = month.split('-').map(Number)
    return new Date(y, m, 0).getDate()
  }, [month])
  const elapsedDays = isCurrentMonth
    ? new Date().getDate()
    : month < getCurrentMonth() ? daysInDisplayMonth : 0
  const monthElapsedPercent = (elapsedDays / daysInDisplayMonth) * 100

  const weeks = useMemo(() => getWeeksInMonth(month), [month])

  const [selectedBudgetItem, setSelectedBudgetItem] = useState<BudgetStatusItem | null>(null)
  const [detailSheetOpen, setDetailSheetOpen] = useState(false)

  // Category drill-down sheet state
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [categorySheetOpen, setCategorySheetOpen] = useState(false)

  const categoryDrilldown = useMemo(() => buildCategoryDrilldown(filtered), [filtered])
  const monthlyTotal = useMemo(
    () => filtered.reduce((s, tx) => s + tx.total_amount, 0),
    [filtered],
  )
  const getCategoryColor = useMemo(() => {
    const m = new Map<string, string>()
    for (const cat of allCategories) {
      if (cat.parent_id === null && cat.color) m.set(cat.name, cat.color)
    }
    return (name: string) => m.get(name) ?? DEFAULT_CATEGORY_COLOR
  }, [allCategories])

  // Re-use the budget map computed for the budget card so the sheet shows the same numbers.
  const budgetByName = useMemo(() => {
    const m = new Map<string, BudgetStatusItem>()
    for (const item of displayBudgetItems) {
      m.set(item.category_name, item)
    }
    return m
  }, [displayBudgetItems])

  if (loading) {
    return (
      <div className="bg-background">
        <MonthSelector month={month} onMonthChange={setMonth} />
        <div className="mx-auto max-w-lg space-y-4 p-4">
        {/* Filter Chips Skeleton */}
        <div className="flex gap-2">
          <Skeleton className="h-7 w-16 rounded-full" />
          <Skeleton className="h-7 w-20 rounded-full" />
          <Skeleton className="h-7 w-16 rounded-full" />
        </div>

        {/* Spending Calendar Skeleton */}
        <Card>
          <CardHeader>
            <CardTitle>支出</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Day labels */}
            <div className="grid grid-cols-7 gap-1 text-center text-[10px] text-muted-foreground">
              {['月', '火', '水', '木', '金', '土', '日'].map((label) => (
                <div key={label}>{label}</div>
              ))}
            </div>
            {/* Calendar grid (5 weeks) */}
            <div className="grid grid-cols-7 gap-1">
              {Array.from({ length: 35 }).map((_, i) => (
                <Skeleton key={i} className="aspect-square rounded-md" />
              ))}
            </div>
            {/* Monthly total */}
            <Skeleton className="mx-auto h-4 w-24" />
          </CardContent>
        </Card>

        {/* Spending Breakdown Skeleton */}
        <Card>
          <CardHeader>
            <CardTitle>カテゴリ別支出</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Skeleton className="h-2 w-full rounded-full" />
            <div className="space-y-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="grid grid-cols-[1fr_auto_auto] items-center gap-3 px-1">
                  <span className="flex items-center gap-1.5">
                    <Skeleton className="size-2 rounded-full" />
                    <Skeleton className="h-4 w-24" />
                  </span>
                  <Skeleton className="h-3 w-8" />
                  <Skeleton className="h-4 w-16" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Budget Summary Skeleton */}
        <Card>
          <CardHeader>
            <CardTitle>予算</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-1.5">
                    <Skeleton className="size-2 rounded-full" />
                    <Skeleton className="h-4 w-20" />
                  </span>
                  <Skeleton className="h-4 w-24" />
                </div>
                <Skeleton className="h-2 w-full" />
                <div className="flex justify-between">
                  <Skeleton className="h-3 w-16" />
                  <Skeleton className="h-3 w-16" />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Recent Transactions Skeleton */}
        <Card>
          <CardHeader>
            <CardTitle>直近の取引</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center justify-between gap-3 px-2 py-3">
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-4 w-10" />
                    <div className="space-y-1">
                      <Skeleton className="h-4 w-28" />
                      <div className="flex items-center gap-1">
                        <Skeleton className="size-2 rounded-full" />
                        <Skeleton className="h-3 w-16" />
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-4 w-16" />
                    <Skeleton className="size-5 rounded-full" />
                    <Skeleton className="size-4" />
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-2 border-t pt-3">
              <Skeleton className="mx-auto h-4 w-20" />
            </div>
          </CardContent>
        </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-background">
      <MonthSelector month={month} onMonthChange={setMonth} />
      <div className="mx-auto max-w-lg space-y-4 p-4">
      {/* Filters */}
      <div className="flex gap-2">
        <FilterChip
          label={SCOPE_LABEL}
          value={scope}
          onChange={setScope}
          options={SCOPE_OPTIONS}
        />
        <FilterChip
          label={EXPENSE_TYPE_LABEL}
          value={expenseType}
          onChange={setExpenseType}
          options={EXPENSE_TYPE_OPTIONS}
        />
        <FilterChip
          label={PAID_BY_LABEL}
          value={paidBy}
          onChange={setPaidBy}
          options={paidByOptions}
        />
      </div>

      {/* Monthly Spending Calendar */}
      <SpendingCalendar
        key={`${month}-${weeklyViewStyle}-${expenseType}`}
        transactions={filtered}
        currentMonth={month}
        variant={weeklyViewStyle}
        weeks={weeks}
      />

      {/* Spending Breakdown by Category */}
      <SpendingBreakdown
        transactions={filtered}
        categories={allCategories}
        onSelectCategory={(name) => {
          setSelectedCategory(name)
          setCategorySheetOpen(true)
        }}
      />

      {/* Category Detail Sheet — Home: 当月の状態をサッと把握 */}
      {selectedCategory && (() => {
        const subs = categoryDrilldown.get(selectedCategory)?.subcategories ?? []
        return (
          <CategoryDetailSheet
            open={categorySheetOpen}
            onOpenChange={setCategorySheetOpen}
            categoryName={selectedCategory}
            categoryColor={getCategoryColor(selectedCategory)}
            categoryAmount={categoryDrilldown.get(selectedCategory)?.amount ?? 0}
            monthlyTotal={monthlyTotal}
            filteredTransactions={filtered}
            currentMonth={month}
          >
            <CategorySummarySection budget={budgetByName.get(selectedCategory)} />
            <CategorySubcategorySection subcategories={subs} />
            <CategoryTopTransactionsSection subcategories={subs} />
            <CategoryTransactionsFooter
              href={`/transactions?month=${month}&category=${encodeURIComponent(selectedCategory)}`}
            />
          </CategoryDetailSheet>
        )
      })()}

      {/* Budget Summary */}
      {displayBudgetItems.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>予算</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {displayBudgetItems.map((item) => {
              const displayRemaining = item.budget_amount - item.spent_amount

              const ratio = item.budget_amount > 0
                ? (item.spent_amount / item.budget_amount) * 100
                : 0
              const progressValue = Math.min(ratio, 100)
              const isOver = displayRemaining < 0

              return (
                <div
                  key={item.category_id}
                  className="cursor-pointer space-y-1 rounded-md -mx-2 px-2 py-1 active:bg-muted/50 transition-colors"
                  onClick={() => {
                    setSelectedBudgetItem(item)
                    setDetailSheetOpen(true)
                  }}
                >
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-1.5 font-medium">
                      {item.color && (
                        <span
                          className="inline-block size-2 shrink-0 rounded-full"
                          style={{ backgroundColor: item.color }}
                        />
                      )}
                      {item.category_name}
                    </span>
                    {isOver ? (
                      <span className="text-destructive font-medium">
                        {formatAmount(Math.abs(displayRemaining))} 超過
                      </span>
                    ) : (
                      <span className="text-muted-foreground">
                        あと {formatAmount(displayRemaining)}
                      </span>
                    )}
                  </div>
                  <div className="relative">
                    <Progress value={progressValue} />
                    {isCurrentMonth && (
                      <div
                        className="absolute -top-0.5 -bottom-0.5 w-px bg-foreground/40"
                        style={{ left: `${monthElapsedPercent}%` }}
                      />
                    )}
                  </div>
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>{formatAmount(item.spent_amount)}</span>
                    <span>{formatAmount(item.budget_amount)}</span>
                  </div>
                </div>
              )
            })}
          </CardContent>
        </Card>
      )}

      {/* Budget Detail Sheet */}
      <BudgetDetailSheet
        open={detailSheetOpen}
        onOpenChange={setDetailSheetOpen}
        budgetItem={selectedBudgetItem}
        transactions={transactions}
        elapsedDays={elapsedDays}
        totalDays={daysInDisplayMonth}
        subcategoryBudgetItems={
          selectedBudgetItem
            ? budgetItems.filter(
                item => item.is_subcategory_budget && item.parent_category_id === selectedBudgetItem.category_id
              )
            : undefined
        }
      />

      {/* Recent Transactions */}
      <Card>
        <CardHeader>
          <CardTitle>直近の取引</CardTitle>
        </CardHeader>
        <CardContent>
          {recentTransactions.length === 0 ? (
            <p className="py-4 text-center text-sm text-muted-foreground">
              取引はありません
            </p>
          ) : (
            <div className="space-y-1">
              {recentTransactions.map((tx) => (
                <TransactionListItem
                  key={tx.id}
                  transaction={tx}
                  to={`/transactions/${tx.id}?month=${tx.date.substring(0, 7)}`}
                />
              ))}
            </div>
          )}

          {recentTransactions.length > 0 && (
            <div className="mt-2 border-t pt-3">
              <Link
                to="/transactions"
                className="flex items-center justify-center gap-1 text-sm text-primary hover:underline"
              >
                すべて見る
                <ChevronRight className="size-4" />
              </Link>
            </div>
          )}
        </CardContent>
      </Card>
      </div>
    </div>
  )
}
